import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import { useMutation, useQuery } from '@tanstack/react-query';

import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import CircularProgress from 'components/shared/CircularProgress';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import Input from 'components/shared/form/Input';
import InputGroup from 'components/shared/form/InputGroup';
import SectionHeader from 'components/shared/form/SectionHeader';
import { STATE_OPTIONS } from 'constants/states';
import { useProfile } from 'context/profile';
import { PERMISSIONS } from 'models/Profile';
import {
  adminCreateLocationQhinConfiguration,
  adminUpdateLocationQhinConfiguration,
} from 'services/api/admin/locationQhinConfigurations';
import { adminLocationsQueryKeys, adminShowLocation } from 'services/api/admin/locations';
import type { LocationQhinConfigurationFields } from 'services/api/fields/admin/location';
import { useToastActions } from 'stores/toastStore';
import { colors } from 'styles/theme/colors';

const NPI_LOOKUP_URL = 'https://npiregistry.cms.hhs.gov/search';

const QHIN_TYPE_OPTIONS = [
  { label: 'Hospital', value: 'hospital' },
  { label: 'Skilled Nursing Facility', value: 'skilled_nursing_facility' },
  { label: 'Nursing Facility', value: 'nursing_facility' },
  { label: 'Home Health Entity', value: 'home_health_entity' },
  { label: 'Health Care Clinic', value: 'health_care_clinic' },
  { label: 'Community Mental Health Center', value: 'community_mental_health_center' },
  { label: 'Renal Dialysis Facility', value: 'renal_dialysis_facility' },
  { label: 'Blood Center', value: 'blood_center' },
  { label: 'Emergency Medical Services Provider', value: 'emergency_medical_services_provider' },
  { label: 'Federally Qualified Health Center', value: 'federally_qualified_health_center' },
  { label: 'Group Practice', value: 'group_practice' },
  { label: 'Pharmacist', value: 'pharmacist' },
  { label: 'Pharmacy', value: 'pharmacy' },
  { label: 'Laboratory', value: 'laboratory' },
  { label: 'Indian Health Service / Tribal Organization', value: 'indian_health_service' },
  { label: 'Rural Health Clinic', value: 'rural_health_clinic' },
  { label: 'Doctor of Medicine or Osteopathy', value: 'doctor_of_medicine_or_osteopathy' },
  { label: 'Doctor of Dental Surgery or Dental Medicine', value: 'doctor_of_dental' },
  { label: 'Doctor of Podiatric Medicine', value: 'doctor_of_podiatric_medicine' },
  { label: 'Doctor of Optometry', value: 'doctor_of_optometry' },
  { label: 'Chiropractor', value: 'chiropractor' },
  { label: 'Other Licensed Health Care Provider', value: 'other_licensed_provider' },
];

type AddressValues = {
  streetAddress: string;
  streetAddressLine2: string;
  city: string;
  state: { label: string; value: string } | null;
  zip: string;
};

type FormValues = {
  legalName: string;
  doingBusinessAsName: string;
  corporateAddress: AddressValues;
  siteOfCareAddress: AddressValues;
  npi: string;
  websiteName: string;
  websiteUrl: string;
  qhinType: { label: string; value: string } | null;
  oid: string;
  evidenceHealthCareProvider: string;
  evidenceCoveredEntity: string;
  lookbackDays: string;
  pollingDays: string;
  pollingIntervalHours: string;
  readyToSubmit: boolean;
};

const INCLUDES = ['locationQhinConfiguration'] as const;

function addressToForm(addr: LocationQhinConfigurationFields['corporateAddress'] | undefined): AddressValues {
  return {
    streetAddress: addr?.streetAddress ?? '',
    streetAddressLine2: addr?.streetAddressLine2 ?? '',
    city: addr?.city ?? '',
    state: addr?.state ? { label: addr.state, value: addr.state } : null,
    zip: addr?.zip ?? '',
  };
}

function formToAddress(addr: AddressValues) {
  return {
    streetAddress: addr.streetAddress,
    streetAddressLine2: addr.streetAddressLine2,
    city: addr.city,
    state: addr.state?.value ?? '',
    zip: addr.zip,
  };
}

function deepEqual(a: unknown, b: unknown) {
  return JSON.stringify(a) === JSON.stringify(b);
}

export default function QhinConfiguration() {
  const { id } = useParams<{ id: string }>();
  const { addToast } = useToastActions();
  const { profile } = useProfile();
  const canEdit = profile.has(PERMISSIONS.adminQhinConfigurationEdit);

  const {
    data: location,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: [...adminLocationsQueryKeys.show(id!), 'qhinConfiguration'],
    queryFn: ({ signal }) => adminShowLocation<'locationQhinConfiguration'>(id!, signal, [...INCLUDES]),
    enabled: !!id,
  });

  const existingConfigFromServer = location?.locationQhinConfiguration;
  const [configId, setConfigId] = useState<string | null>(null);

  const existingConfigId = configId || existingConfigFromServer?.id || null;

  const locationAddressToForm = (addr: typeof location extends { address: infer A } ? A : any): AddressValues => ({
    streetAddress: addr?.address1 ?? '',
    streetAddressLine2: addr?.address2 ?? '',
    city: addr?.city ?? '',
    state: addr?.state ? { label: addr.state, value: addr.state } : null,
    zip: addr?.zip ?? '',
  });

  const buildDefaults = (): FormValues => ({
    legalName: location?.name ?? '',
    doingBusinessAsName: location?.name ?? '',
    corporateAddress: locationAddressToForm(location?.address),
    siteOfCareAddress: locationAddressToForm(location?.address),
    npi: '',
    websiteName: '',
    websiteUrl: '',
    qhinType: null,
    oid: '',
    evidenceHealthCareProvider: '',
    evidenceCoveredEntity: '',
    lookbackDays: '14',
    pollingDays: '7',
    pollingIntervalHours: '24',
    readyToSubmit: false,
  });

  const buildFromConfig = (config: LocationQhinConfigurationFields): FormValues => ({
    legalName: config.legalName ?? '',
    doingBusinessAsName: config.doingBusinessAsName ?? '',
    corporateAddress: addressToForm(config.corporateAddress),
    siteOfCareAddress: addressToForm(config.siteOfCareAddress),
    npi: config.npi ?? '',
    websiteName: config.websiteName ?? '',
    websiteUrl: config.websiteUrl ?? '',
    qhinType: QHIN_TYPE_OPTIONS.find((o) => o.value === config.qhinType) ?? null,
    oid: config.oid ?? '',
    evidenceHealthCareProvider: config.evidenceHealthCareProvider ?? '',
    evidenceCoveredEntity: config.evidenceCoveredEntity ?? '',
    lookbackDays: String(config.lookbackDays ?? 14),
    pollingDays: String(config.pollingDays ?? 7),
    pollingIntervalHours: String(config.pollingIntervalHours ?? 24),
    readyToSubmit: config.readyToSubmit,
  });

  const serverValues = useRef<FormValues>(buildDefaults());
  const [values, setValues] = useState<FormValues>(buildDefaults());

  useEffect(() => {
    if (!location) return;

    const initial = existingConfigFromServer ? buildFromConfig(existingConfigFromServer) : buildDefaults();
    serverValues.current = initial;
    setValues(initial);
  }, [location]); // eslint-disable-line react-hooks/exhaustive-deps

  const isDirty = !deepEqual(values, serverValues.current);

  const npiError = values.npi
    ? !/^\d+$/.test(values.npi)
      ? 'NPI must contain only digits'
      : values.npi.length !== 10
        ? 'NPI must be exactly 10 digits'
        : undefined
    : undefined;

  const setValue = <K extends keyof FormValues>(key: K, value: FormValues[K]) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  };

  const setAddressValue = (
    addressKey: 'corporateAddress' | 'siteOfCareAddress',
    field: keyof AddressValues,
    value: any
  ) => {
    setValues((prev) => ({
      ...prev,
      [addressKey]: { ...prev[addressKey], [field]: value },
    }));
  };

  const buildPayload = () => ({
    locationId: id!,
    legalName: values.legalName,
    doingBusinessAsName: values.doingBusinessAsName,
    corporateAddress: formToAddress(values.corporateAddress),
    siteOfCareAddress: formToAddress(values.siteOfCareAddress),
    npi: values.npi,
    websiteName: values.websiteName,
    websiteUrl: values.websiteUrl,
    qhinType: values.qhinType?.value ?? '',
    oid: values.oid,
    evidenceHealthCareProvider: values.evidenceHealthCareProvider,
    evidenceCoveredEntity: values.evidenceCoveredEntity,
    lookbackDays: parseInt(values.lookbackDays, 10) || 14,
    pollingDays: parseInt(values.pollingDays, 10) || 7,
    pollingIntervalHours: parseInt(values.pollingIntervalHours, 10) || 24,
    readyToSubmit: values.readyToSubmit,
  });

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async () => {
      if (existingConfigId) {
        return adminUpdateLocationQhinConfiguration(existingConfigId, buildPayload());
      }
      return adminCreateLocationQhinConfiguration(buildPayload());
    },
    onSuccess: (data) => {
      if (data?.id) setConfigId(data.id);
      refetch();
      addToast({ text: 'QHIN Configuration saved successfully' });
    },
    onError: () => {
      addToast({ text: 'An error occurred while saving. Please try again.' });
    },
  });

  const hasValidationErrors = !!npiError;
  const handleSave = () => mutateAsync();
  const handleDiscard = () => setValues({ ...serverValues.current });

  if (isLoading) {
    return <CenteredSpinner />;
  }

  return (
    <PageContainer>
      <Header>
        <HeaderTop>
          <TitleArea>
            <Title>QHIN Configuration</Title>
            <Subtitle>{location?.name}</Subtitle>
          </TitleArea>
          {canEdit && (
            <ButtonGroup>
              <Button variant='ghost' onClick={handleDiscard} disabled={!isDirty || isPending}>
                Discard Changes
              </Button>
              <Button onClick={handleSave} disabled={!isDirty || isPending || hasValidationErrors}>
                Save
              </Button>
            </ButtonGroup>
          )}
        </HeaderTop>
      </Header>

      <Content>
        <SectionHeader>Organization</SectionHeader>

        <InputGroup title='Legal Name'>
          <Input
            value={values.legalName}
            onChange={(e) => setValue('legalName', e.target.value)}
            placeholder='Legal Name'
            disabled={!canEdit}
          />
        </InputGroup>

        <InputGroup title='Doing Business As Name'>
          <Input
            value={values.doingBusinessAsName}
            onChange={(e) => setValue('doingBusinessAsName', e.target.value)}
            placeholder='Doing Business As'
            disabled={!canEdit}
          />
        </InputGroup>

        <SectionHeader>Corporate Address</SectionHeader>
        <AddressFields
          values={values.corporateAddress}
          onChange={(field, value) => setAddressValue('corporateAddress', field, value)}
          disabled={!canEdit}
        />

        <SectionHeader>Site of Care Address</SectionHeader>
        <Note>
          If your organization does not have a physical site of care address because it provides care in the
          patient&apos;s home or using a mobile vehicle, please indicate. If your organization only provides care
          virtually, please indicate.
        </Note>
        <AddressFields
          values={values.siteOfCareAddress}
          onChange={(field, value) => setAddressValue('siteOfCareAddress', field, value)}
          disabled={!canEdit}
        />

        <SectionHeader>Identification</SectionHeader>

        <InputGroup
          title='NPI'
          error={npiError}
          badge={
            <ExternalLink href={NPI_LOOKUP_URL} target='_blank' rel='noopener noreferrer'>
              Look up NPI
            </ExternalLink>
          }>
          <Input
            value={values.npi}
            onChange={(e) => setValue('npi', e.target.value)}
            placeholder='National Provider Identifier'
            disabled={!canEdit}
          />
        </InputGroup>

        <InputGroup title='OID'>
          <Input
            value={values.oid}
            onChange={(e) => setValue('oid', e.target.value)}
            placeholder='Object Identifier'
            disabled={!canEdit}
          />
        </InputGroup>

        <SectionHeader>Website</SectionHeader>

        <InlineGroup>
          <InputGroup title='Website Name'>
            <Input
              value={values.websiteName}
              onChange={(e) => setValue('websiteName', e.target.value)}
              placeholder='Website Name'
              disabled={!canEdit}
            />
          </InputGroup>
          <InputGroup title='Website URL'>
            <Input
              value={values.websiteUrl}
              onChange={(e) => setValue('websiteUrl', e.target.value)}
              placeholder='https://'
              disabled={!canEdit}
            />
          </InputGroup>
        </InlineGroup>

        <SectionHeader>Type</SectionHeader>

        <InputGroup title='Type of Health Care Provider'>
          <Select
            options={QHIN_TYPE_OPTIONS}
            value={values.qhinType}
            onChange={(v) => setValue('qhinType', v)}
            getOptionValue={(o) => o.value}
            getOptionLabel={(o) => o.label}
            placeholder='Select Type'
            isDisabled={!canEdit}
          />
        </InputGroup>

        <SectionHeader>Evidence</SectionHeader>
        <Note>
          <ExternalLink
            href='https://data.cms.gov/provider-characteristics/medicare-provider-supplier-enrollment/medicare-fee-for-service-public-provider-enrollment'
            target='_blank'
            rel='noopener noreferrer'>
            Look up provider enrollment on CMS
          </ExternalLink>
        </Note>

        <InputGroup title='Evidence: Health Care Provider'>
          <Input
            value={values.evidenceHealthCareProvider}
            onChange={(e) => setValue('evidenceHealthCareProvider', e.target.value)}
            placeholder='Evidence that entrant is a health care provider'
            disabled={!canEdit}
          />
        </InputGroup>

        <InputGroup title='Evidence: HIPAA Covered Entity'>
          <Input
            value={values.evidenceCoveredEntity}
            onChange={(e) => setValue('evidenceCoveredEntity', e.target.value)}
            placeholder='Evidence that entrant is a covered entity under HIPAA'
            disabled={!canEdit}
          />
        </InputGroup>

        <SectionHeader>Polling Configuration</SectionHeader>

        <InlineGroup>
          <InputGroup title='Lookback Days'>
            <Input
              type='number'
              value={values.lookbackDays}
              onChange={(e) => setValue('lookbackDays', e.target.value)}
              placeholder='14'
              disabled={!canEdit}
            />
          </InputGroup>
          <InputGroup title='Polling Days'>
            <Input
              type='number'
              value={values.pollingDays}
              onChange={(e) => setValue('pollingDays', e.target.value)}
              placeholder='7'
              disabled={!canEdit}
            />
          </InputGroup>
          <InputGroup title='Polling Interval (Hours)'>
            <Input
              type='number'
              value={values.pollingIntervalHours}
              onChange={(e) => setValue('pollingIntervalHours', e.target.value)}
              placeholder='24'
              disabled={!canEdit}
            />
          </InputGroup>
        </InlineGroup>

        <SectionHeader>Submission</SectionHeader>

        <CheckboxRow>
          <input
            type='checkbox'
            checked={values.readyToSubmit}
            onChange={(e) => setValue('readyToSubmit', e.target.checked)}
            disabled={!canEdit}
          />
          <CheckboxLabel>Ready to submit</CheckboxLabel>
        </CheckboxRow>
      </Content>
    </PageContainer>
  );
}

function AddressFields({
  values,
  onChange,
  disabled,
}: {
  values: AddressValues;
  onChange: (field: keyof AddressValues, value: any) => void;
  disabled: boolean;
}) {
  return (
    <>
      <InlineGroup>
        <InputGroup title='Street 1'>
          <Input
            value={values.streetAddress}
            onChange={(e) => onChange('streetAddress', e.target.value)}
            placeholder='Street 1'
            disabled={disabled}
          />
        </InputGroup>
        <InputGroup title='Street 2 (optional)'>
          <Input
            value={values.streetAddressLine2}
            onChange={(e) => onChange('streetAddressLine2', e.target.value)}
            placeholder='Street 2'
            disabled={disabled}
          />
        </InputGroup>
      </InlineGroup>
      <InlineGroup>
        <InputGroup title='City'>
          <Input
            value={values.city}
            onChange={(e) => onChange('city', e.target.value)}
            placeholder='City'
            disabled={disabled}
          />
        </InputGroup>
        <InlineGroup>
          <InputGroup title='State'>
            <Select
              options={STATE_OPTIONS}
              value={values.state}
              onChange={(v) => onChange('state', v)}
              getOptionValue={(o) => o.value}
              getOptionLabel={(o) => o.label}
              placeholder='State'
              isDisabled={disabled}
            />
          </InputGroup>
          <InputGroup title='Zip'>
            <Input
              value={values.zip}
              onChange={(e) => onChange('zip', e.target.value)}
              placeholder='Zip'
              disabled={disabled}
            />
          </InputGroup>
        </InlineGroup>
      </InlineGroup>
    </>
  );
}

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
`;

const Header = styled.div`
  flex: 0 0 auto;
  background: white;
  border-bottom: 1px solid ${colors.black10};
`;

const HeaderTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 24px 24px 16px;
`;

const TitleArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 700;
  margin: 0;
  color: ${colors.black};
`;

const Subtitle = styled.h3`
  font-size: 16px;
  font-weight: 700;
  margin: 0;
  color: ${colors.primaryBlue};
`;

const Content = styled.div`
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 24px;
  max-width: 640px;
`;

const InlineGroup = styled.div`
  display: flex;
  gap: 16px;

  > * {
    flex: 1;
  }
`;

const Note = styled.p`
  font-size: 12px;
  line-height: 20px;
  color: ${colors.black50};
  margin: 0 0 16px;
`;

const ExternalLink = styled.a`
  color: ${colors.primaryBlue};
  text-decoration: none;
  font-size: 12px;

  &:hover {
    text-decoration: underline;
  }
`;

const CheckboxRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
`;

const CheckboxLabel = styled.span`
  font-size: 14px;
  color: ${colors.black};
`;

const CenteredSpinner = styled(CircularProgress)`
  margin: 48px auto;
  display: block;
`;
